import { Injectable } from '@angular/core';
import { CRouteList } from '@app/route-list.const';
import { differenceInDays } from 'date-fns';
import { IOrderItemSelection } from '../checkout/model/checkout-order-item-selection.model';
import { DateFormatService } from '../date/date-format.service';
import { IProductInfo } from '../product/models/product-info.model';
import { IProductPackageItem } from '../product/models/product-package-item.model';
import { IProduct } from '../product/models/product.model';
import { ICheckoutProductCategory } from '../venue-config/models/checkout-product-category.model';
import { IVenueConfig } from '../venue-config/models/venue-config.model';
import { IVenueHomeProductWidget } from '../venue-config/models/venue-home-product-widget.model';
import { IProductAnalytics } from './analytics-progress/product-analytics.model';
import { IAnalyticsItem, IOrderSelection } from './analytics_item.model';
import { IMatomoEcommerceItem, IMatomoSetEcommerceViewItem } from './matomo-analytics.model';
import { IMetaContentItem } from './meta.model';
import { ITikTokContents, ITikTokParams } from './tik-tok-analytics.model';
import { IOrderItem } from '@core/order/models/order-item.model';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsMappingService {
  readonly cRoutes = CRouteList;

  constructor(private dateFormatService: DateFormatService) {}

  /** View list has its own map requirements. */
  mapProductListToGA4AnalyticsViewList(items: IProductAnalytics[], venueCurrency: string): IAnalyticsItem[] {
    return items.map((item: IProductAnalytics, index: number) => {
      const aItem: IAnalyticsItem = {
        item_id: item.id,
        item_name: item.name,
        currency: venueCurrency,
        index,
        item_brand: item.type as string,
        item_list_id: item.item_list_id,
        item_list_name: item.item_list_name,
        price: item.priceFrom
      };
      return aItem;
    });
  }

  /** Maps a products ticket items through to Analytics Items. */
  mapProductToAnalyticsItem(
    item: IProduct | undefined,
    pageName: string,
    addedFrom: string,
    index: number
  ): IAnalyticsItem[] {
    const aItems: IAnalyticsItem[] = [];
    if (!item) {
      return aItems;
    }

    item.products.forEach((pInfo: IProductInfo) => {
      const aItem: IAnalyticsItem = {
        item_id: item.parentProductId as string,
        item_name: item.parentProductName as string,
        index,
        item_brand: item.type as string,
        item_list_id: pageName,
        item_list_name: addedFrom,
        item_variant: pInfo.name,
        price: Math.abs(pInfo.cost)
      };

      aItems.push(aItem);
    });

    return aItems;
  }

  /** Maps a products ticket items through to TikTok Items. */
  mapProductToTikTokItem(item: IProduct, pageName: string, currency: string): ITikTokParams {
    const aItems: ITikTokContents[] = [];

    item.products.forEach((pInfo: IProductInfo) => {
      const aItem: ITikTokContents = {
        content_type: 'product',
        content_id: pInfo.id,
        content_name: pInfo.name,
        content_category: item.name,
        price: Math.abs(pInfo.cost)
      };

      aItems.push(aItem);
    });

    return {
      content_id: item.id,
      content_type: 'product_group',
      content_name: pageName,
      contents: aItems,
      currency
    };
  }

  /** Maps the order Items through to analytics items including cost of price and quantity. */
  mapOrderItemsToAnalyticsItem(
    items: IOrderItem[],
    venueCurrency: string,
    pageName?: string,
    addedFrom?: string,
    isOldQuantityRelevant = false
  ): IAnalyticsItem[] {
    const aItems: IAnalyticsItem[] = [];
    const start = this.dateFormatService.getDateWithoutTime(new Date());
    items.forEach((oItem: IOrderItem) => {
      const quantity = Math.abs(
        isOldQuantityRelevant && oItem.oldQuantity ? oItem.quantity - oItem.oldQuantity : oItem.quantity
      );
      const aItem: IAnalyticsItem = {
        item_id: oItem.parentProductId,
        item_name: oItem.parentName as string,
        currency: venueCurrency,
        item_brand: oItem.productType,
        item_list_id: pageName,
        item_list_name: addedFrom,
        price: oItem.cost,
        quantity,
        item_variant: oItem.product.name,
        booking_date: this.dateFormatService.dateToStringDateOnlyHyphen(oItem.bookingDate),
        booking_days_ahead: differenceInDays(oItem.bookingDate, start)
      };
      if (oItem.orderSelections) {
        aItem.order_selections = this.mapOrderSelectionPackageItems(oItem);
      }

      aItems.push(aItem);
    });

    return aItems;
  }

  /** For party package inclusions find the relevant names and adds them to array */
  mapOrderSelectionPackageItems(item: IOrderItem): IOrderSelection[] | undefined {
    return item.orderSelections
      ?.map((oSelection: IOrderItemSelection) => {
        const findPackageItem = item.product.packageItems?.find(
          (pItem: IProductPackageItem) => pItem.productId === oSelection.parentProductId
        );
        const findVariation = findPackageItem?.variations.find(
          (variation: IProductInfo) => variation.id === oSelection.productId
        );
        if (findPackageItem && findVariation) {
          const orderSelection: IOrderSelection = {
            quantity: oSelection.quantity,
            item_name: findPackageItem.name,
            item_variant: findVariation.name
          };
          return orderSelection;
        }

        return undefined;
      })
      .filter((item: IOrderSelection | undefined): item is IOrderSelection => !!item);
  }

  /** Maps the Product through to Matomo analytics event properties for EcommerceViewItem */
  mapProductToMatomoSetEcommerceViewItem(product: IProduct, category: string | undefined): IMatomoSetEcommerceViewItem {
    return {
      productSku: product.parentProductId?.toString() || '',
      productName: product.name,
      productCategory: category ?? '',
      price: product.cost
    };
  }

  /** Maps the order Items through to Matomo analytics event properties for EcommerceItem */
  mapOrderItemsToMatomoEcommerceItems(items: IOrderItem[]): IMatomoEcommerceItem[] {
    const aItems: IMatomoEcommerceItem[] = [];
    items.forEach((oItem: IOrderItem) => {
      const quantity = Math.abs(oItem.quantity);
      const aItem: IMatomoEcommerceItem = {
        productSku: this.getUniqueProductSku(oItem),
        productName: `${oItem.parentName} - ${oItem.product.name}`,
        productCategory: '',
        price: Math.abs(oItem.cost * quantity),
        quantity: quantity
      };

      aItems.push(aItem);
    });

    return aItems;
  }

  /** Maps the order Items through to analytics items including cost of price and quantity. */
  mapOrderItemsToTikTokContents(items: IOrderItem[], venueCurrency: string, pageName?: string): ITikTokParams {
    const aItems: ITikTokContents[] = [];
    items.forEach((oItem: IOrderItem) => {
      const quantity = Math.abs(oItem.oldQuantity ? oItem.quantity - oItem.oldQuantity : oItem.quantity);
      const aItem: ITikTokContents = {
        content_id: oItem.parentProductId,
        content_type: 'product',
        content_category: oItem.productType,
        content_name: oItem.parentName as string,
        price: Math.abs(oItem.cost * quantity),
        quantity
      };

      aItems.push(aItem);
    });

    return {
      content_type: 'product_group',
      contents: aItems,
      content_category: pageName,
      currency: venueCurrency,
      price: aItems.reduce((acc: number, cont: ITikTokContents) => {
        return (cont.price || 0) + acc;
      }, 0)
    };
  }

  /** Maps the order Items through to meta content items. */
  mapOrderItemsToMetaContentItems(items: IOrderItem[]): IMetaContentItem[] {
    const aItems: IMetaContentItem[] = [];
    items.forEach((oItem: IOrderItem) => {
      const quantity = Math.abs(oItem.oldQuantity ? oItem.quantity - oItem.oldQuantity : oItem.quantity);
      aItems.push({
        id: oItem.parentProductId,
        quantity
      } as IMetaContentItem);
    });

    return aItems;
  }

  /** Outputs strings of product Id's from the venue config - initially to use with Facebook meta tag. */
  mapProductIds(page_path: string, venueConfig: IVenueConfig | undefined): string[] {
    const content_ids: string[] = [];
    if (venueConfig) {
      if (page_path.includes(this.cRoutes.home)) {
        venueConfig.homePage.widgets?.forEach((widget: IVenueHomeProductWidget) => {
          if (widget.type === 'product-carousel') {
            const idList = widget.fullProducts?.slice(0, 8).map((product: IProduct) => product.id) || [];
            content_ids.push(...idList);
          } else if (widget.type === 'featured-products') {
            const idList = widget.fullProducts?.map((product: IProduct) => product.id) || [];
            content_ids.push(...idList);
          }
        });
      } else if (page_path.includes(this.cRoutes.products)) {
        venueConfig.productsPage.productCategories?.forEach((pCat: ICheckoutProductCategory) => {
          pCat.fullProducts?.forEach((prod: IProduct) => content_ids.push(prod.id));
        });
      }
    }

    return content_ids;
  }

  /**
   * Matomo will treat items with the same productId as the same productSku regardless of date/time.
   * To circumvent, use a composite key comprising of productId, bookingDate, and sessionTime (if available)
   * @example 445566_20240101_1000
   */
  private getUniqueProductSku(orderItem: IOrderItem): string {
    const bookingDate = this.dateFormatService.toDateIndex(orderItem.bookingDate);
    const productSku = [orderItem.productId];

    if (bookingDate) {
      productSku.push(bookingDate.toString());
    }

    if (orderItem.session?.startTime) {
      const sessionTime = this.dateFormatService.hourStringToNumber(orderItem.session?.startTime);
      productSku.push(sessionTime.toString());
    }

    return productSku.join('_');
  }
}
